// Generated Convex read model -> the pure Phase-1 plan surface models.
//
// Coverage arrives already derived by the server through the kernel. This adapter
// supplies presentation-only concerns: local-time labels, product-language copy and
// opaque deep links. It never counts commitments or decides a capability.

import {
  instant,
  localDateOf,
  timeZone,
  type PersonId,
  type TimeZoneId,
} from "@herefor/domain-kernel";
import { USER_VISIBLE_STATES } from "@herefor/product-language";

import type { LivePlanResult } from "../../convex/plan";
import {
  formatWindowInZone,
  sortNeedsAttention,
  userVisibleStateKey,
  type AvailabilityRowView,
  type EventView,
  type NeedView,
} from "./planData";

export interface LivePlanViews {
  readonly events: readonly EventView[];
  readonly needs: readonly NeedView[];
  readonly attention: readonly NeedView[];
  /**
   * Needs belonging to no event, chronologically (bead hf-qyq).
   *
   * The week list carries them beside the events, whatever their coverage. Before
   * that they had one home — the attention list, which keeps only the states that
   * still want a person — so covering one removed it from every plan surface while
   * leaving it live, addressable and in the calendar feed.
   */
  readonly standalone: readonly NeedView[];
  /** Null is an authorization decision; an empty array means authorised but empty. */
  readonly availability: readonly AvailabilityRowView[] | null;
}

export function toLivePlanViews(result: LivePlanResult): LivePlanViews {
  const zone = timeZone(result.timeZone);
  const needs = result.needs.map((need) => toNeedView(need, zone, result.publicPlanRef));
  const needsByEvent = groupNeedsByEvent(result.needs, needs);

  const events = result.events.map((event): EventView => ({
    id: event.eventId,
    title: event.title,
    href: `/c/${result.publicPlanRef}/events/${event.eventId}`,
    windowLabel:
      event.startsAt === undefined || event.endsAt === undefined
        ? null
        : formatWindowInZone(zone, instant(event.startsAt), instant(event.endsAt)),
    dateLabel: event.startsAt === undefined ? null : localDateOf(instant(event.startsAt), zone),
    locationLabel: event.locationLabel ?? null,
    // Ordering only; the labels above own the display (bead hf-qyq).
    startsAt: event.startsAt === undefined ? null : instant(event.startsAt),
    stateKey:
      event.state === "completed" ? "done" : event.state === "cancelled" ? "cancelled" : null,
    stateLabel:
      event.state === "completed"
        ? USER_VISIBLE_STATES.done.label
        : event.state === "cancelled"
          ? USER_VISIBLE_STATES.cancelled.label
          : null,
    needs: needsByEvent.get(event.eventId) ?? [],
  }));

  const availability =
    result.availability === null
      ? null
      : result.availability.map((row): AvailabilityRowView => ({
          key: row.availabilityWindowId,
          name: row.displayName,
          dateLabel: localDateOf(instant(row.startsAt), zone),
          windowLabel: formatWindowInZone(zone, instant(row.startsAt), instant(row.endsAt)),
          helpModes: row.helpModes,
          recordedByName: row.recordedByName,
        }));

  const standalone = result.needs
    .flatMap((need, index) => {
      const view = needs[index];
      return need.eventId === undefined && view !== undefined ? [view] : [];
    })
    .sort((left, right) => left.startsAt - right.startsAt);

  return { events, needs, attention: sortNeedsAttention(needs), standalone, availability };
}

function toNeedView(
  need: LivePlanResult["needs"][number],
  zone: TimeZoneId,
  publicPlanRef: string,
): NeedView {
  const startsAt = instant(need.startsAt);
  const stateKey = userVisibleStateKey(need.coverage.status);
  const copy = USER_VISIBLE_STATES[stateKey];

  return {
    id: need.supportNeedId,
    label: need.label,
    href: `/c/${publicPlanRef}/needs/${need.supportNeedId}`,
    stateKey,
    stateLabel: copy.label,
    stateExplanation: copy.explanation,
    counts:
      need.coverage.requiredCapacity > 1
        ? {
            confirmed: need.coverage.confirmedCount,
            required: need.coverage.requiredCapacity,
          }
        : null,
    windowLabel: formatWindowInZone(zone, startsAt, instant(need.endsAt)),
    dateLabel: localDateOf(startsAt, zone),
    isPast: need.coverage.isPast,
    startsAt,
    responsible: need.responsible.map((person) => ({
      personId: person.personId as PersonId,
      name: person.displayName,
      recordedByName: person.recordedByName,
      agreementSource: person.agreementSource,
    })),
  };
}

function groupNeedsByEvent(
  rawNeeds: LivePlanResult["needs"],
  views: readonly NeedView[],
): ReadonlyMap<string, readonly NeedView[]> {
  const grouped = new Map<string, NeedView[]>();
  rawNeeds.forEach((need, index) => {
    const eventId = need.eventId;
    const view = views[index];
    if (eventId === undefined || view === undefined) return;
    const current = grouped.get(eventId) ?? [];
    current.push(view);
    grouped.set(eventId, current);
  });
  return grouped;
}
